import { LocalDate } from "@js-joda/core";
import { toEnglishOrdinalDay } from "./tips.date.logic";

interface FormatHoursParams {
  readonly hourLabel: string;
  readonly hours: number;
  readonly minuteLabel: string;
}

interface FormatMonthTitleParams {
  readonly date: LocalDate;
  readonly locale: string;
}

interface FormatDateParams {
  readonly dateValue: string;
  readonly locale: string;
}

function capitalizeFirst(value: string): string {
  return value.length > 0 ? value[0].toUpperCase() + value.slice(1) : value;
}

function formatMonthDayDate({ dateValue, locale }: FormatDateParams): string {
  const date = new Date(dateValue);
  const month = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
  const day = date.getDate();

  if (locale.startsWith("en")) {
    return `${toEnglishOrdinalDay(day)} of ${month}`;
  }

  return `${day} ${month}`;
}

export function formatHours({
  hourLabel,
  hours,
  minuteLabel,
}: FormatHoursParams): string {
  const totalMinutes = Math.round(hours * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) return `${wholeHours}`;
  return `${wholeHours}${hourLabel} ${minutes}${minuteLabel}`;
}

export function formatMonthTitle({
  date,
  locale,
}: FormatMonthTitleParams): string {
  const monthDate = new Date(date.year(), date.monthValue() - 1, 1);
  const month = new Intl.DateTimeFormat(locale, { month: "long" }).format(
    monthDate,
  );
  return `${capitalizeFirst(month)} ${date.year()}`;
}

export function formatReportDate(params: FormatDateParams): string {
  return formatMonthDayDate(params);
}

export function formatTransactionDate(params: FormatDateParams): string {
  return formatMonthDayDate(params);
}

export function isInMonth({
  dateValue,
  monthDate,
}: {
  readonly dateValue: string;
  readonly monthDate: LocalDate;
}): boolean {
  const date = new Date(dateValue);
  return (
    date.getFullYear() === monthDate.year() &&
    date.getMonth() + 1 === monthDate.monthValue()
  );
}

export function toMonthLocalDate({
  dateValue,
}: {
  readonly dateValue: string;
}): LocalDate {
  const date = new Date(dateValue);
  return LocalDate.of(date.getFullYear(), date.getMonth() + 1, 1);
}
